import DOMPurify from "dompurify";
import { marked } from "marked";
import type { RetrievedChunkSnapshot } from "../db/types";
import {
  buildFile,
  cleanCopyName,
  dataTables,
  downloadBlob,
  downloadName,
  extractFileBlocks,
  fileKind,
  generatedRevision,
  isDocxRevision,
  isFileRevision,
  isPdfRevision,
  repairPipeTables,
  resolveCitations,
  rowsFromRawText,
  tablesFromJson,
  type BuildOptions,
  type FileBlock,
} from "../api/fileBlocks";
import { conversationFileBlocks, findOriginal } from "../state/originals";
import { parseRevision, type DocxEdit } from "../api/docxEdits";
import { docxTemplateRequest, fieldExample, withSignature, xlsxTemplateRequest, type DocTemplate } from "../api/docTemplates";
import { resolveFieldsNow } from "../api/epflDirectory.svelte";
import { tr } from "../i18n/i18n.svelte";

// Remplace chaque bloc ```file:nom.ext rendu par marked par une carte
// « Télécharger ». Même principe que mermaid.ts : DOM brut après le
// {@html}, refait à chaque rendu. Le contenu reste consultable dans un
// <details> replié sous la carte.

const KIND_LABEL: Record<string, string> = {
  pdf: "PDF",
  xlsx: "Excel",
  doc: "Word",
  csv: "CSV",
};

/** Ce dont la construction d'un fichier a besoin hors du message : le fichier
 *  d'origine d'une révision, le serveur pour un PDF modifié. */
function buildOptions(onWarning: (message: string) => void): BuildOptions {
  return {
    findOriginal,
    conversation: conversationFileBlocks,
    onWarning,
    // Chargés au clic : la session et l'appel au serveur ne servent qu'ici.
    editPdf: async (original: Uint8Array, edits: DocxEdit[]) => {
      const [{ app }, { editPdfOnServer }] = await Promise.all([import("../state/app.svelte"), import("../api/pdfEdit")]);
      if (!app.config) throw new Error("configuration absente");
      return editPdfOnServer(original, edits, app.config);
    },
  };
}

function isFileCode(code: Element): boolean {
  return Array.from(code.classList).some((c) => /^language-file:/i.test(c));
}

// État de l'aperçu « Contenu » (ouvert, défilement), gardé hors du DOM :
// pendant le streaming, chaque morceau de texte reconstruit la réponse et
// donc la carte, qui se refermerait aussitôt. Clé = message + rang du bloc ;
// la réponse en cours (« pending ») passe son état à la réponse enregistrée.
interface PreviewState {
  open: boolean;
  scrollTop: number;
  /** Défilement en bas : suivre le texte qui s'écrit. */
  follow: boolean;
}
const previewStates = new Map<string, PreviewState>();

export interface FileCardsScope {
  /** Identifiant du message, ou de la réponse en cours. */
  messageKey: string;
  /** Clé de la réponse en cours de la même conversation, dont l'état est
   *  repris quand elle devient le message enregistré. */
  pendingKey: string;
}

function previewState(scope: FileCardsScope, i: number, name: string): PreviewState {
  const key = `${scope.messageKey}|${i}|${name}`;
  let state = previewStates.get(key);
  if (!state) {
    const pendingKey = `${scope.pendingKey}|${i}|${name}`;
    state = previewStates.get(pendingKey) ?? { open: false, scrollTop: 0, follow: true };
    previewStates.delete(pendingKey);
    previewStates.set(key, state);
  }
  return state;
}

/** Déclenche `run` dès l'appui (souris, doigt) ou au clic clavier.
 *
 *  Pendant la rédaction, la réponse est redessinée à chaque morceau de texte et
 *  la carte avec elle : entre l'appui et le relâchement, le bouton a déjà été
 *  remplacé et le clic natif se perd. Un fichier fini ne se téléchargeait
 *  donc qu'à la fin de toute la réponse — avec dix fichiers, après le
 *  dixième (22.10.2026). Même parade que pour le <summary> plus bas. */
function onActivate(btn: HTMLButtonElement, run: () => void | Promise<void>): void {
  btn.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    void run();
  });
  // Clavier (Entrée, Espace) : detail === 0 ; le clic souris est déjà traité.
  btn.addEventListener("click", (e) => {
    if (e.detail === 0) void run();
  });
}

/** Interface v3 (spec §11) : l'aperçu s'ouvre dans le panneau de droite. */
export interface FilePreviewHooks {
  isOpen(key: string): boolean;
  /** Ouvre l'aperçu de ce bloc, ou le ferme s'il est déjà ouvert. */
  toggle(key: string, block: FileBlock, retrieved: RetrievedChunkSnapshot[] | null | undefined): void;
  /** Le bloc a changé (texte qui arrive) : l'aperçu ouvert le suit. */
  update(key: string, block: FileBlock, retrieved: RetrievedChunkSnapshot[] | null | undefined): void;
}

export function renderFileCards(
  container: HTMLElement,
  content: string,
  retrieved: RetrievedChunkSnapshot[] | null | undefined,
  scope: FileCardsScope,
  onError: (message: string) => void,
  onWarning: (message: string) => void = onError,
  preview?: FilePreviewHooks,
): void {
  const codes = Array.from(container.querySelectorAll("pre > code")).filter(isFileCode);
  if (codes.length === 0) return;
  // Même ordre que le rendu : le n-ième bloc de code file: du DOM est le
  // n-ième bloc file: du markdown.
  const blocks = extractFileBlocks(content);
  codes.forEach((code, i) => {
    const block = blocks[i];
    const pre = code.parentElement;
    if (block && pre && !pre.closest(".file-card")) {
      if (preview) mountCardV3(block, pre, retrieved, `${scope.messageKey}|${i}|${block.name}`, preview, onWarning);
      else mountCard(block, pre, retrieved, previewState(scope, i, block.name), onError, onWarning);
    }
  });
}

// ── Carte de fichier v3 (spec §11.1–11.3) ──────────────────────────────────

const SVG: Record<string, string> = {
  fileText: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/>',
  eye: '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>',
  download: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>',
  refresh: '<path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>',
};

function svgIcon(name: keyof typeof SVG, size: number): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${SVG[name]}</svg>`;
}

/** Révision d'un fichier généré plus haut dans la conversation : le fichier
 *  révisé en entier, pour l'étiquette, l'aperçu et les boutons. */
function effectiveBlock(block: FileBlock): FileBlock {
  if (!block.complete || !isFileRevision(block)) return block;
  const generated = generatedRevision(block, conversationFileBlocks());
  return generated ? { ...block, body: generated.text } : block;
}

/** Le modèle EPFL dont part le fichier, s'il en a un. */
export function blockTemplate(block: FileBlock): DocTemplate | null {
  block = effectiveBlock(block);
  const request = block.ext === "docx" ? docxTemplateRequest(block.body) : block.ext === "xlsx" ? xlsxTemplateRequest(block.body) : null;
  return request?.template ?? null;
}

/** La personne au nom de qui un fichier au modèle EPFL est établi (Figma
 *  26-H) : le signataire, sinon l'expéditeur, tels que le fichier les
 *  portera (l'usager, ou la personne nommée trouvée dans l'annuaire). Vide
 *  sans modèle, en cours de rédaction, ou sans coordonnées connues. */
export function blockAuthor(block: FileBlock): string {
  if (!block.complete) return "";
  block = effectiveBlock(block);
  const request = block.ext === "docx" ? docxTemplateRequest(block.body) : block.ext === "xlsx" ? xlsxTemplateRequest(block.body) : null;
  if (!request) return "";
  const { fields } = resolveFieldsNow(request.fields);
  return (fields.get("signataire") ?? "").split("\n")[0].trim() || (fields.get("expediteur") ?? "").trim();
}

/** Ce qu'est le fichier, sous son nom (spec §11.1, §11.3). */
export function formatLabel(block: FileBlock): string {
  if (!block.complete) {
    // Jetons écrits jusqu'ici, ~4 caractères par jeton (comme les jetons de
    // réflexion sous la réponse en cours).
    const tokens = Math.round(block.body.length / 4);
    if (tokens === 0) return tr({ fr: "Rédaction en cours…", en: "Writing…" });
    const n = tokens.toLocaleString(tr({ fr: "fr-CH", en: "en-GB" }));
    return tr({ fr: `Rédaction en cours… · ${n} tokens`, en: `Writing… · ${n} tokens` });
  }
  block = effectiveBlock(block);
  if (isDocxRevision(block)) {
    return tr({
      fr: "Révision de votre document, avec suivi des modifications",
      en: "Revision of your document, with tracked changes",
    });
  }
  if (isPdfRevision(block)) return tr({ fr: "Révision de votre PDF", en: "Revision of your PDF" });
  const template = blockTemplate(block);
  if (template) return `${template.label} (.${template.ext})`;
  switch (fileKind(block.ext)) {
    case "doc":
      return block.ext === "docx"
        ? tr({ fr: "Document Word (.docx)", en: "Word document (.docx)" })
        : tr({ fr: "Document Word (.doc)", en: "Word document (.doc)" });
    case "pdf":
      return tr({ fr: "Document PDF", en: "PDF document" });
    case "xlsx":
      return tr({ fr: "Classeur Excel (.xlsx)", en: "Excel workbook (.xlsx)" });
    case "csv":
      return tr({ fr: "Tableau CSV", en: "CSV table" });
    case "text":
      return tr({ fr: `Fichier texte (.${block.ext})`, en: `Text file (.${block.ext})` });
    default:
      return tr({
        fr: `Format .${block.ext || "?"} non pris en charge : demandez un document Word, PDF, Excel ou CSV.`,
        en: `.${block.ext || "?"} format not supported: ask for a Word, PDF, Excel or CSV document.`,
      });
  }
}

/** Construit et télécharge le fichier ; rend false en cas d'échec. */
export async function downloadFile(
  block: FileBlock,
  retrieved: RetrievedChunkSnapshot[] | null | undefined,
  onWarning: (message: string) => void,
  clean = false,
): Promise<boolean> {
  try {
    const blob = await buildFile(block, retrieved, { ...buildOptions(onWarning), clean });
    downloadBlob(blob, clean ? cleanCopyName(block) : downloadName(block));
    return true;
  } catch (err) {
    console.warn("[file] build failed", err);
    return false;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
}

function tableHtml(rows: string[][], title: string | null = null): string {
  if (rows.length === 0) return "";
  const [head, ...body] = rows;
  const cells = (r: string[], tag: "th" | "td") => `<tr>${r.map((c) => `<${tag}>${escapeHtml(c)}</${tag}>`).join("")}</tr>`;
  return `${title ? `<h3>${escapeHtml(title)}</h3>` : ""}<table><thead>${cells(head, "th")}</thead><tbody>${body.map((r) => cells(r, "td")).join("")}</tbody></table>`;
}

const EPFL_LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 182.4 53" fill="#ff0000"><polygon points="0 21.6 11.43 21.6 11.43 9.8 38.34 9.8 38.34 0 0 0 0 21.6"/><polygon points="0 53 38.34 53 38.34 43.2 11.43 43.2 11.43 31.4 0 31.4 0 53"/><rect x="11.43" y="21.6" width="24.61" height="9.8"/><path d="M86,4.87a16.12,16.12,0,0,0-5.68-3.53A23.76,23.76,0,0,0,71.82,0H48.14V53H59.57V31.4H71.82a23.76,23.76,0,0,0,8.46-1.34A16.12,16.12,0,0,0,86,26.53a13.43,13.43,0,0,0,3.19-5,17.38,17.38,0,0,0,0-11.62A13.52,13.52,0,0,0,86,4.87ZM78,18.73a5.7,5.7,0,0,1-2.26,1.8,11.33,11.33,0,0,1-3.27.85,32,32,0,0,1-3.86.22H59.57V9.8h9.05a32,32,0,0,1,3.86.22,11,11,0,0,1,3.27.86A5.59,5.59,0,0,1,78,12.67a5,5,0,0,1,.86,3A5,5,0,0,1,78,18.73Z"/><polygon points="155.47 43.2 155.47 0 144.04 0 144.04 53 182.38 53 182.38 43.2 155.47 43.2"/><polygon points="97.42 21.6 108.85 21.6 108.85 9.8 135.76 9.8 135.76 0 97.42 0 97.42 21.6"/><rect x="97.42" y="31.4" width="11.43" height="21.6"/><rect x="108.85" y="21.6" width="24.61" height="9.8"/></svg>`;
const EPFL_LOGO_SRC = `data:image/svg+xml,${encodeURIComponent(EPFL_LOGO_SVG)}`;

/** Aperçu d'un fichier au modèle EPFL : en-tête (logo, unité ou titre) et
 *  pied de page simplifiés autour du contenu. Un champ que le modèle n'a pas
 *  donné montre le texte d'exemple du modèle, grisé : à compléter. */
class TemplatePage {
  constructor(
    private template: DocTemplate,
    private fields: Map<string, string>,
  ) {}

  field(key: string): string {
    const value = this.fields.get(key);
    const lines = (s: string) => escapeHtml(s).replace(/\n/g, "<br>");
    if (value === undefined) {
      const example = fieldExample(this.template, key);
      return example ? `<span class="tp-ex" title="${escapeHtml(tr({ fr: "À compléter", en: "To be completed" }))}">${lines(example)}</span>` : "";
    }
    return lines(value);
  }

  /** Lignes du pied de page, sans les vides. */
  private stack(keys: string[], extra: string[] = []): string {
    return [...keys.map((k) => this.field(k)), ...extra].filter(Boolean).join("<br>");
  }

  unitLines(): string {
    return [`<strong>${this.field("unite")}</strong>`, this.field("sous-unite")].filter((l) => l && l !== "<strong></strong>").join("<br>");
  }

  wrap(content: string, headRight: string): string {
    const id = this.template.id;
    const left = id.endsWith("-corp")
      ? `<span class="tp-school">École polytechnique fédérale de Lausanne</span><br><strong>${this.field("faculte")}</strong>`
      : id.startsWith("excel-")
        ? `<strong>${this.field("faculte")}</strong>`
        : `<span class="tp-square"></span>${this.unitLines()}`;
    const middle = this.stack(["sigle", "expediteur", "bureau", "station"], ["CH-1015 Lausanne"]);
    const right = this.stack(["telephone", "email", "site"]);
    return `<div class="tp"><div class="tp-head"><img class="tp-logo" src="${EPFL_LOGO_SRC}" alt="EPFL"><div class="tp-unit">${headRight}</div></div><div class="tp-content">${content}</div><div class="tp-foot"><div>${left}</div><div>${middle}</div><div>${right}</div></div></div>`;
  }
}

/** Personne nommée que l'annuaire ne connaît pas : dit dans l'aperçu, pas
 *  seulement au téléchargement. */
function unknownSigner(name: string): string {
  if (!name) return "";
  return `<p class="tp-warn">${escapeHtml(
    tr({
      fr: `« ${name} » est introuvable dans l'annuaire EPFL, ou plusieurs personnes portent ce nom : l'unité et le pied de page restent les vôtres. Donnez son nom complet tel qu'il figure dans l'annuaire.`,
      en: `“${name}” is not in the EPFL directory, or several people share this name: the unit and footer stay yours. Give their full name as it appears in the directory.`,
    }),
  )}</p>`;
}

/** Le contenu mis en forme pour l'aperçu (spec §11.2) : titres, paragraphes,
 *  listes, tableaux, gras, comme dans Word — jamais le Markdown ni le HTML
 *  bruts. Une révision montre ses remplacements, un tableur ses feuilles. */
export function previewHtml(block: FileBlock, retrieved: RetrievedChunkSnapshot[] | null | undefined): string {
  block = effectiveBlock(block);
  const revision = (isDocxRevision(block) || isPdfRevision(block)) && parseRevision(block.body);
  if (revision) {
    const items = revision.edits
      .map((e) => `<li><del>${escapeHtml(e.find)}</del> → <ins>${escapeHtml(e.replace)}</ins></li>`)
      .join("");
    return DOMPurify.sanitize(
      `<p><strong>${escapeHtml(tr({ fr: "Modifications proposées", en: "Proposed changes" }))}</strong> · ${escapeHtml(revision.source)}</p><ol>${items}</ol>`,
    );
  }
  const body = repairPipeTables(resolveCitations(block.body, retrieved));
  const word = block.ext === "docx" ? docxTemplateRequest(body) : null;
  if (word) {
    const resolved = resolveFieldsNow(word.fields);
    const page = new TemplatePage(word.template, resolved.fields);
    const text = withSignature(word.template, word.fields, resolved.fields, word.body);
    const isLetter = word.template.id.startsWith("lettre-");
    const letterHead = isLetter
      ? `<div class="tp-addr">${page.field("destinataire")}</div><div class="tp-date">${page.field("date")}</div><p class="tp-obj">${page.field("concerne")}</p><p>${page.field("salutation")}</p>`
      : "";
    const letterFoot = isLetter ? `<p class="tp-sign">${page.field("signataire")}</p>` : "";
    return DOMPurify.sanitize(
      unknownSigner(resolved.unknown) +
        page.wrap(`${letterHead}${marked.parse(text, { async: false, gfm: true, breaks: true }) as string}${letterFoot}`, page.unitLines()),
    );
  }
  const sheet = block.ext === "xlsx" ? xlsxTemplateRequest(body) : null;
  if (sheet) {
    const resolved = resolveFieldsNow(sheet.fields);
    const page = new TemplatePage(sheet.template, resolved.fields);
    const tables = dataTables(body).map((tb) => tableHtml(tb.rows, tb.title)).join("");
    return DOMPurify.sanitize(unknownSigner(resolved.unknown) + page.wrap(tables, `<strong>${page.field("titre")}</strong>`));
  }
  switch (fileKind(block.ext)) {
    case "xlsx":
      return DOMPurify.sanitize(dataTables(body).map((tb) => tableHtml(tb.rows, tb.title)).join("") || `<p>${escapeHtml(body)}</p>`);
    case "csv": {
      const tables = tablesFromJson(body);
      return DOMPurify.sanitize(tables ? tables.map((tb) => tableHtml(tb.rows, tb.title)).join("") : tableHtml(rowsFromRawText(body)));
    }
    case "text":
      if (block.ext !== "md") return `<pre class="fc-plain">${escapeHtml(body)}</pre>`;
      break;
  }
  return DOMPurify.sanitize(marked.parse(body, { async: false, gfm: true, breaks: true }) as string);
}

/** Échecs de création, par carte : l'alerte et « Réessayer » survivent au
 *  rendu suivant de la réponse. */
const failedCards = new Set<string>();

function smallButton(label: string, icon: keyof typeof SVG, ariaLabel?: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "btn small";
  b.innerHTML = `${svgIcon(icon, 16)}<span></span>`;
  b.querySelector("span")!.textContent = label;
  if (ariaLabel) b.setAttribute("aria-label", ariaLabel);
  return b;
}

/** Carte du DS : icône, nom et format, puis « Aperçu » et « Télécharger »
 *  (Boutons Secondaires Petit). Plus de code source dans la réponse :
 *  l'aperçu mis en forme s'ouvre dans le panneau de droite. */
function mountCardV3(
  block: FileBlock,
  pre: HTMLElement,
  retrieved: RetrievedChunkSnapshot[] | null | undefined,
  key: string,
  preview: FilePreviewHooks,
  onWarning: (message: string) => void,
): void {
  const filename = downloadName(block);
  const revision = isDocxRevision(effectiveBlock(block));
  const supported = !!fileKind(block.ext);

  const wrap = document.createElement("div");
  wrap.className = "file-card-wrap";
  const card = document.createElement("div");
  card.className = "file-card fc3";
  card.classList.toggle("stacked", revision);
  pre.replaceWith(wrap);
  wrap.append(card);

  const icon = document.createElement("span");
  icon.className = "fc-icon";
  // La carte est recréée à chaque morceau de texte reçu : un spinner neuf
  // repartirait de zéro et semblerait figé. Calé sur l'horloge (délai négatif),
  // il reprend là où le précédent en était (1,6 s : multiple des deux durées
  // de v3.css, normale et animations réduites).
  icon.innerHTML = block.complete
    ? svgIcon("fileText", 24)
    : `<span class="spinner" aria-hidden="true" style="animation-delay: -${Date.now() % 1600}ms"></span>`;

  const text = document.createElement("div");
  text.className = "fc-text";
  const name = document.createElement("span");
  name.className = "fc-name";
  name.textContent = filename;
  name.title = filename;
  const format = document.createElement("span");
  format.className = "fc-format";
  // v3 seulement : « · Au nom de Marie Lambert » (Figma 26-H).
  const author = blockAuthor(block);
  format.textContent = formatLabel(block) + (author ? tr({ fr: ` · Au nom de ${author}`, en: ` · On behalf of ${author}` }) : "");
  text.append(name, format);

  const actions = document.createElement("div");
  actions.className = "fc-actions";

  const open = preview.isOpen(key);
  const previewBtn = smallButton(tr({ fr: "Aperçu", en: "Preview" }), "eye", tr({ fr: `Afficher l'aperçu de ${filename}`, en: `Show the preview of ${filename}` }));
  previewBtn.dataset.previewKey = key;
  previewBtn.setAttribute("aria-pressed", String(open));
  onActivate(previewBtn, () => preview.toggle(key, block, retrieved));
  actions.append(previewBtn);
  if (open) preview.update(key, block, retrieved);

  const failed = failedCards.has(key);
  const run = async (btn: HTMLButtonElement, clean = false) => {
    if (btn.disabled) return;
    btn.disabled = true;
    const ok = await downloadFile(block, retrieved, onWarning, clean);
    btn.disabled = false;
    if (ok) failedCards.delete(key);
    else failedCards.add(key);
    // « Télécharger » devient « Réessayer » après un échec, et inversement.
    if (!revision) {
      btn.innerHTML = svgIcon(ok ? "download" : "refresh", 16) + "<span></span>";
      btn.querySelector("span")!.textContent = ok ? tr({ fr: "Télécharger", en: "Download" }) : tr({ fr: "Réessayer", en: "Try again" });
    }
    showError(wrap, card, key);
  };

  if (revision) {
    // Révision d'un Word joint : la copie modifiée et le suivi des modifications.
    const cleanBtn = smallButton(tr({ fr: "Copie modifiée", en: "Edited copy" }), "download", tr({ fr: `Télécharger ${cleanCopyName(block)}`, en: `Download ${cleanCopyName(block)}` }));
    const trackBtn = smallButton(tr({ fr: "Suivi des modifications", en: "Tracked changes" }), "download", tr({ fr: `Télécharger ${filename}`, en: `Download ${filename}` }));
    cleanBtn.disabled = trackBtn.disabled = !block.complete;
    onActivate(cleanBtn, () => run(cleanBtn, true));
    onActivate(trackBtn, () => run(trackBtn));
    actions.append(cleanBtn, trackBtn);
  } else if (supported) {
    const dl = failed
      ? smallButton(tr({ fr: "Réessayer", en: "Try again" }), "refresh")
      : smallButton(tr({ fr: "Télécharger", en: "Download" }), "download", tr({ fr: `Télécharger ${filename}`, en: `Download ${filename}` }));
    dl.disabled = !block.complete;
    onActivate(dl, () => run(dl));
    actions.append(dl);
  }

  card.append(icon, text, actions);
  showError(wrap, card, key);
}

/** Alerte d'erreur Elements sous la carte (spec §11.3), tant que la création
 *  a échoué ; le bouton redevient « Télécharger » au prochain rendu réussi. */
function showError(wrap: HTMLElement, card: HTMLElement, key: string): void {
  wrap.querySelector(".fc-error")?.remove();
  if (!failedCards.has(key)) return;
  const alert = document.createElement("div");
  alert.className = "alert danger fc-error";
  alert.setAttribute("role", "alert");
  alert.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M9 9l6 6M15 9l-6 6"/></svg><div><strong></strong><span></span></div>`;
  alert.querySelector("strong")!.textContent = tr({ fr: "Le document n'a pas pu être créé", en: "The document could not be created" });
  alert.querySelector("span")!.textContent = tr({
    fr: " Réessayez. Si le problème persiste, utilisez « Signaler une erreur » sous la réponse : le texte reste disponible dans l'aperçu.",
    en: " Try again. If the problem persists, use “Report an error” under the answer: the text stays available in the preview.",
  });
  card.after(alert);
}

/** La carte prend la place du <pre>, qui passe dans son aperçu replié. */
function mountCard(
  block: FileBlock,
  pre: HTMLElement,
  retrieved: RetrievedChunkSnapshot[] | null | undefined,
  state: PreviewState,
  onError: (message: string) => void,
  onWarning: (message: string) => void,
): void {
  const kind = fileKind(block.ext);
  const filename = downloadName(block);

  const card = document.createElement("div");
  card.className = "file-card";
  pre.replaceWith(card);

  const head = document.createElement("div");
  head.className = "file-card-head";

  const name = document.createElement("span");
  name.className = "file-card-name";
  name.textContent = `📄 ${filename}`;
  name.title = filename;

  const revision = isDocxRevision(effectiveBlock(block));
  // Révision qui prolonge la précédente : on le dit, l'usager sait sur quelle
  // version portent les changements.
  const followUp = (revision || isPdfRevision(effectiveBlock(block))) && !!parseRevision(block.body)?.continue;
  const label = document.createElement("span");
  label.className = "file-card-kind";
  label.textContent = revision
    ? followUp
      ? tr({ fr: "Word, suivi des modifications (suite)", en: "Word, tracked changes (continued)" })
      : tr({ fr: "Word, suivi des modifications", en: "Word, tracked changes" })
    : isPdfRevision(effectiveBlock(block))
      ? followUp
        ? tr({ fr: "PDF modifié (suite)", en: "PDF, further edits" })
        : tr({ fr: "PDF d'origine modifié", en: "Original PDF, edited" })
      : kind
        ? (KIND_LABEL[kind] ?? block.ext.toUpperCase())
        : `.${block.ext}`;

  const btn = document.createElement("button");
  btn.type = "button";
  // Révision d'un Word : deux versions, le suivi des modifications et la copie
  // modifiée (changements déjà acceptés, prête à envoyer).
  const idle = revision
    ? tr({ fr: "⬇ Suivi des modifications", en: "⬇ Tracked changes" })
    : tr({ fr: "⬇ Télécharger", en: "⬇ Download" });
  const cleanBtn = revision ? document.createElement("button") : null;
  const cleanIdle = tr({ fr: "⬇ Copie modifiée", en: "⬇ Edited copy" });
  if (cleanBtn) {
    cleanBtn.type = "button";
    cleanBtn.className = "file-card-alt";
    cleanBtn.textContent = cleanIdle;
    cleanBtn.title = tr({
      fr: "Le document avec les modifications déjà acceptées, sans suivi des modifications",
      en: "The document with the changes already accepted, without tracked changes",
    });
    cleanBtn.disabled = !block.complete;
    onActivate(cleanBtn, async () => {
      if (cleanBtn.disabled) return;
      cleanBtn.disabled = true;
      cleanBtn.textContent = "…";
      try {
        downloadBlob(
          await buildFile(block, retrieved, { ...buildOptions(onWarning), clean: true }),
          cleanCopyName(block),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.warn("[file] build failed", err);
        onError(`${tr({ fr: "Création du fichier impossible", en: "Could not create the file" })} ${cleanCopyName(block)} : ${message}`);
      } finally {
        cleanBtn.disabled = false;
        cleanBtn.textContent = cleanIdle;
      }
    });
  }
  if (!block.complete) {
    btn.textContent = tr({ fr: "rédaction…", en: "writing…" });
    btn.disabled = true;
  } else if (!kind) {
    btn.textContent = tr({ fr: "format non pris en charge", en: "unsupported format" });
    btn.disabled = true;
  } else {
    btn.textContent = idle;
    onActivate(btn, async () => {
      if (btn.disabled) return;
      btn.disabled = true;
      btn.textContent = "…";
      try {
        downloadBlob(await buildFile(block, retrieved, buildOptions(onWarning)), filename);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.warn("[file] build failed", err);
        onError(`${tr({ fr: "Création du fichier impossible", en: "Could not create the file" })} ${filename} : ${message}`);
      } finally {
        btn.disabled = false;
        btn.textContent = idle;
      }
    });
  }

  head.append(name, label, btn);
  if (cleanBtn) head.append(cleanBtn);

  const preview = document.createElement("details");
  preview.className = "file-card-preview";
  const summary = document.createElement("summary");
  summary.textContent = tr({ fr: "Contenu", en: "Content" });
  preview.append(summary, pre);
  preview.open = state.open;
  preview.addEventListener("toggle", () => {
    state.open = preview.open;
  });
  // Pendant le streaming, la carte est recréée à chaque morceau de texte :
  // entre l'appui et le relâchement, le <summary> a déjà été remplacé et le
  // clic natif se perd. On bascule donc dès l'appui, et on neutralise le clic
  // souris qui suivrait (le clavier, detail === 0, garde le comportement natif).
  summary.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    state.open = !preview.open;
    preview.open = state.open;
  });
  summary.addEventListener("click", (e) => {
    if (e.detail > 0) e.preventDefault();
  });
  pre.addEventListener("scroll", () => {
    state.scrollTop = pre.scrollTop;
    state.follow = pre.scrollTop + pre.clientHeight >= pre.scrollHeight - 8;
  });

  card.append(head, preview);
  // Après insertion : la hauteur du <pre> n'est connue qu'une fois rendu.
  if (state.open) pre.scrollTop = state.follow ? pre.scrollHeight : state.scrollTop;
}
